/**
 * Error_Handler.ts
 *
 * Single source of truth for extracting and displaying backend error messages.
 *
 * Backend error shapes handled:
 *   Platform API  → { message: string }  |  { title: string }  |  { errors: [{ message }] }
 *   Suki API      → { message: string }  |  { error: string }
 *   Network error → Error.message
 */

import { showToast } from '../components/toastComponent';
import type { QuotaBlockedMeta } from '../schemas/catalog/meter';

const MAX_LENGTH = 300;
const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

/* Characters that are safe to display in a toast — blocks HTML/script injection */
const BLOCKED = /<|>|javascript:|data:/i;

function isSafeString(val: unknown): val is string {
  return typeof val === 'string' && val.trim().length > 0 && !BLOCKED.test(val);
}

/**
 * Extracts the most specific error message from an axios error object.
 * Always returns the real backend message when available.
 * Falls back to `fallback` only when no usable message is found.
 */
export function getApiErrorMessage(error: unknown, fallback = DEFAULT_FALLBACK): string {
  if (!error || typeof error !== 'object') return fallback;

  const err = error as any;
  const data = err?.response?.data;

  /* ── Priority order: pick the most specific message ── */

  // 1. data.message  (most APIs)
  if (isSafeString(data?.message)) return data.message.trim().slice(0, MAX_LENGTH);

  // 2. data.error.message  (sk-catalog-api envelope: { error: { code, message } })
  if (isSafeString(data?.error?.message)) return data.error.message.trim().slice(0, MAX_LENGTH);

  // 3. data.errors[0].message  (validation error arrays)
  const firstError = Array.isArray(data?.errors) ? data.errors[0] : null;
  if (isSafeString(firstError?.message)) return firstError.message.trim().slice(0, MAX_LENGTH);

  // 4. data.title  (Platform API RFC 7807 shape)
  if (isSafeString(data?.title)) return data.title.trim().slice(0, MAX_LENGTH);

  // 5. data.error  (Suki API short error string)
  if (isSafeString(data?.error)) return data.error.trim().slice(0, MAX_LENGTH);

  // 5. Network / JS error message
  if (isSafeString(err?.message)) return err.message.trim().slice(0, MAX_LENGTH);

  return fallback;
}

/**
 * Shows a backend error message in an error toast.
 * Use this in every catch block instead of manually extracting error.response.data.message.
 *
 * @param error   - The caught error (any shape)
 * @param title   - Toast heading (e.g. 'Login Failed', 'Save failed')
 * @param fallback - Optional custom fallback when no backend message is found
 */
export function handleApiError(error: unknown, title: string, fallback = DEFAULT_FALLBACK): void {
  const message = getApiErrorMessage(error, fallback);
  showToast('error', title, message);
}

/* ─────────────────────────────────────────
   Quota / metering (HTTP 402 AT_CAP_BLOCKED)

   The catalog API blocks a metered action at the plan cap with:
     402 { error: { code:'AT_CAP_BLOCKED', message, meta: {...} } }
   These helpers let any screen detect that specific case and read the
   recharge/upgrade payload, without each catch block re-parsing axios errors.
───────────────────────────────────────── */

export const AT_CAP_BLOCKED = 'AT_CAP_BLOCKED';

/** True when the error is a plan-limit block (402 AT_CAP_BLOCKED). */
export function isAtCapError(error: unknown): boolean {
  const code = (error as any)?.response?.data?.error?.code;
  const status = (error as any)?.response?.status;
  return code === AT_CAP_BLOCKED || status === 402;
}

/** The recharge/upgrade payload carried by an at-cap error, if present. */
export function getQuotaBlockedMeta(error: unknown): QuotaBlockedMeta | null {
  const meta = (error as any)?.response?.data?.error?.meta;
  return meta && typeof meta === 'object' ? (meta as QuotaBlockedMeta) : null;
}

/* ─────────────────────────────────────────
   SKCATALOG-CC-002 typed errors

   Three of this CR's refusals must NOT be shown as a generic error toast:

     422 hsn_sac_required     — SKCATALOG-DB-001 §1 asks for an inline,
                                actionable block on the HSN/SAC field
                                ("Set an HSN/SAC code before publishing —
                                required for GST"), never a generic validation
                                toast. A toast is dismissed and forgotten; the
                                operator needs the message next to the input
                                they have to fill in.
     403 field_locked         — the field is brand-locked. The right feedback is
                                the field rendering read-only with a lock glyph,
                                not an error after the fact.
     409 illegal_transition   — the console should not have OFFERED the
                                transition (DB-001 §4). If one is refused anyway
                                (a stale list, a concurrent edit), the meta names
                                the legal destinations so the UI can recover.

   Each helper reads the same `{ error: { code, message, meta } }` envelope the
   quota helpers above already use, so no screen re-parses axios errors.
───────────────────────────────────────── */

export const HSN_SAC_REQUIRED = 'hsn_sac_required';
export const FIELD_LOCKED = 'field_locked';
export const ILLEGAL_TRANSITION = 'illegal_transition';
export const PRICE_FIELD_NOT_LOCKABLE = 'price_field_not_lockable';

function errorCode(error: unknown): string | null {
  const code = (error as any)?.response?.data?.error?.code;
  return typeof code === 'string' ? code : null;
}

function errorMeta(error: unknown): Record<string, unknown> | null {
  const meta = (error as any)?.response?.data?.error?.meta;
  return meta && typeof meta === 'object' ? (meta as Record<string, unknown>) : null;
}

/** 422 — a taxable item was published with no HSN/SAC code. */
export function isHsnSacRequiredError(error: unknown): boolean {
  return errorCode(error) === HSN_SAC_REQUIRED;
}

/** 403 — a location-scoped write to a brand-locked field. */
export function isFieldLockedError(error: unknown): boolean {
  return errorCode(error) === FIELD_LOCKED;
}

/** 400 — someone tried to lock a price field (the console should not offer it). */
export function isPriceFieldNotLockableError(error: unknown): boolean {
  return errorCode(error) === PRICE_FIELD_NOT_LOCKABLE;
}

/** 409 — an illegal lifecycle transition. */
export function isIllegalTransitionError(error: unknown): boolean {
  return errorCode(error) === ILLEGAL_TRANSITION;
}

/** The field a `field_locked` refusal names, so the UI can point at it. */
export function getLockedFieldKey(error: unknown): string | null {
  const key = errorMeta(error)?.fieldKey;
  return typeof key === 'string' ? key : null;
}

/** `{ from, to, allowed }` from an `illegal_transition` refusal. */
export function getIllegalTransitionMeta(
  error: unknown,
): { from: string; to: string; allowed: string[] } | null {
  const meta = errorMeta(error);
  if (!meta) return null;
  const { from, to, allowed } = meta as any;
  if (typeof from !== 'string' || typeof to !== 'string') return null;
  return { from, to, allowed: Array.isArray(allowed) ? allowed : [] };
}
